import {EventMetricsSummary} from "../../stores/domainStore/pages/models/eventMetricsSummary";
import {formatThousands} from "../stringUtils";
import {MetricsColumn, MetricsRow} from "./metricTypes";

/**
 * The data that we display in the events metrics table
 */
export class EventsMetricsRow extends MetricsRow {
  scheduledEvents: number = 0;
  paidEvents: number = 0;

  totalBookings: number = 0;
  confirmedBookings: number = 0;

  totalCapacity: number = 0;
  attendance: number = 0;

  estimatedIncome: number = 0;
  confirmedIncome: number = 0;

  totalTransaction: number = 0;
  transactionValue: number = 0;

  numberOfBookableEvents: number = 0;
  numberOfFeaturedEvents: number = 0;
}

export type EventMetricsColumn = MetricsColumn<
  EventMetricsSummary,
  EventsMetricsRow
>;

/**
 * How we calculate + display each enhanced metric for the events table
 */
export const EnhancedMetricsTypes: EventMetricsColumn[] = [
  {
    dataField: "village",
    headerLabel: "Village",
    sort: true,
    formatter: (data: EventsMetricsRow) =>
      !data.isTotalsRow && data.village
        ? data.village.name
        : "All Selected Villages",
    rawValue: (data: EventsMetricsRow) => data.village?.name ?? ""
  },
  {
    dataField: "scheduledEvents",
    headerLabel: "Total Events",
    sort: true,
    valueInit: (events, dest) => {
      dest.scheduledEvents = events.length;
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.scheduledEvents),
    rawValue: (data: EventsMetricsRow) => data.scheduledEvents
  },
  {
    dataField: "paidEvents",
    headerLabel: "Paid Events",
    tooltip:
      "Number of events with a fixed cost, excluding cost at own expense.",
    sort: true,
    valueInit: (events, dest) => {
      dest.paidEvents = events.filter((e) => (e.price?.amount ?? 0) > 0).length;
    },
    formatter: (data: EventsMetricsRow) => formatThousands(data.paidEvents),
    rawValue: (data: EventsMetricsRow) => data.paidEvents
  },
  {
    dataField: "totalBookings",
    headerLabel: "Total Bookings",
    tooltip:
      "Number of confirmed, cancelled, and waitlisted bookings. Includes events with unlimited capacity.",
    sort: true,
    valueInit: (events, dest) => {
      dest.totalBookings = events.reduce((acc, ev) => {
        return (
          acc +
          (ev.totalBookings ?? 0) +
          (ev.totalCancelledBookings ?? 0) +
          (ev.totalWaitlistedBookings ?? 0)
        );
      }, 0);
    },
    formatter: (data: EventsMetricsRow) => formatThousands(data.totalBookings),
    rawValue: (data: EventsMetricsRow) => data.totalBookings
  },
  {
    dataField: "confirmedBookings",
    headerLabel: "Confirmed Bookings",
    tooltip: "Number of confirmed bookings - fixed capacity events only.",
    sort: true,
    valueInit: (events, dest) => {
      dest.confirmedBookings = events
        .filter((ev) => (ev.capacity ?? 0) > 0)
        .reduce((acc, ev) => {
          return acc + (ev.totalBookings ?? 0);
        }, 0);
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.confirmedBookings),
    rawValue: (data: EventsMetricsRow) => data.confirmedBookings
  },
  {
    dataField: "capacity",
    headerLabel: "Total Capacity",
    sort: true,
    valueInit: (events, dest) => {
      dest.totalCapacity = events.reduce((acc, ev) => {
        return acc + (ev.capacity ?? 0);
      }, 0);
    },
    formatter: (data: EventsMetricsRow) => formatThousands(data.totalCapacity),
    rawValue: (data: EventsMetricsRow) => data.totalCapacity
  },
  {
    dataField: "attendance",
    headerLabel: "Attendance",
    tooltip:
      "Percentage of capacity with confirmed bookings. Fixed capacity events only.",
    sort: true,
    valueInit: (events, dest) => {
      let totalCapacity = 0;
      let totalConfirmedBookings = 0;
      events.forEach((ev) => {
        if (ev.capacity) {
          totalCapacity += ev.capacity;
          totalConfirmedBookings += ev.totalBookings ?? 0;
        }
      });
      if (totalCapacity === 0) {
        dest.attendance = 0;
      } else {
        dest.attendance = Math.floor(
          (totalConfirmedBookings / totalCapacity) * 100
        );
      }
    },
    formatter: (data: EventsMetricsRow) => data.attendance + "%",
    rawValue: (data: EventsMetricsRow) => data.attendance
  },
  {
    dataField: "bookableEvents",
    headerLabel: "Bookable Events",
    sort: true,
    valueInit: (events, dest) => {
      dest.numberOfBookableEvents = events.reduce(
        (acc: number, ev: EventMetricsSummary) => {
          return acc + (ev.isBookable ? 1 : 0);
        },
        0
      );
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.numberOfBookableEvents),
    rawValue: (data: EventsMetricsRow) => data.numberOfBookableEvents
  },
  {
    dataField: "featuredEvents",
    headerLabel: "Featured Events",
    sort: true,
    valueInit: (events, dest) => {
      dest.numberOfFeaturedEvents = events.reduce(
        (acc: number, ev: EventMetricsSummary) => {
          return acc + (ev.featured ? 1 : 0);
        },
        0
      );
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.numberOfFeaturedEvents),
    rawValue: (data: EventsMetricsRow) => data.numberOfFeaturedEvents
  },
  {
    dataField: "estimatedIncome",
    headerLabel: "Estimated Income",
    tooltip:
      "Total capacity * price. Events with unlimited capacity are not included.",
    sort: true,
    valueInit: (events, dest) => {
      const estimatedIncome = events.reduce((acc, ev) => {
        return acc + (ev.price?.amount ?? 0) * (ev.capacity ?? 0);
      }, 0);
      dest.estimatedIncome = Math.round(estimatedIncome);
    },
    formatter: (data: EventsMetricsRow) =>
      "$" + formatThousands(data.estimatedIncome),
    rawValue: (data: EventsMetricsRow) => data.estimatedIncome
  },
  {
    dataField: "confirmedIncome",
    headerLabel: "Confirmed Income",
    tooltip:
      'Confirmed bookings * price. Does not consider "is paid", or if the event has finished.',
    sort: true,
    valueInit: (events, dest) => {
      const confirmedIncome = events.reduce((acc, ev) => {
        return acc + (ev.price?.amount ?? 0) * (ev.totalBookings ?? 0);
      }, 0);
      dest.confirmedIncome = Math.round(confirmedIncome);
    },
    formatter: (data: EventsMetricsRow) =>
      "$" + formatThousands(data.confirmedIncome),
    rawValue: (data: EventsMetricsRow) => data.confirmedIncome
  },
  {
    dataField: "totalTransaction",
    headerLabel: "Transactions Count",
    tooltip: "Number of transactions, including those yet to be charged.",
    sort: true,
    valueInit: (events, dest) => {
      const totalTransaction = events.reduce((acc, ev) => {
        return acc + (ev.totalConfirmedPayOnAccountBookings ?? 0);
      }, 0);
      dest.totalTransaction = Math.round(totalTransaction);
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.totalTransaction),
    rawValue: (data: EventsMetricsRow) => data.totalTransaction
  },
  {
    dataField: "transactionValue",
    headerLabel: "Transactions Value",
    tooltip: "Value of all transactions, including those yet to be charged.",
    sort: true,
    valueInit: (events, dest) => {
      const transactionValue = events.reduce((acc, ev) => {
        return (
          acc +
          (ev.totalConfirmedPayOnAccountBookings ?? 0) * (ev.price?.amount ?? 0)
        );
      }, 0);

      dest.transactionValue = Math.round(transactionValue);
    },
    formatter: (data: EventsMetricsRow) =>
      "$" + formatThousands(data.transactionValue),
    rawValue: (data: EventsMetricsRow) => data.transactionValue
  }
];

/**
 * How we calculate + display each enhanced metric for the events table
 */
export const EngageMetricsTypes: EventMetricsColumn[] = [
  {
    dataField: "village",
    headerLabel: "Village",
    sort: true,
    formatter: (data: EventsMetricsRow) =>
      !data.isTotalsRow && data.village
        ? data.village.name
        : "All Selected Villages",
    rawValue: (data: EventsMetricsRow) => data.village?.name ?? ""
  },
  {
    dataField: "scheduledEvents",
    headerLabel: "Total Events",
    sort: true,
    valueInit: (events, dest) => {
      dest.scheduledEvents = events.length;
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.scheduledEvents),
    rawValue: (data: EventsMetricsRow) => data.scheduledEvents
  },
  {
    dataField: "bookableEvents",
    headerLabel: "Bookable Events",
    sort: true,
    valueInit: (events, dest) => {
      dest.numberOfBookableEvents = events.reduce(
        (acc: number, ev: EventMetricsSummary) => {
          return acc + (ev.isBookable ? 1 : 0);
        },
        0
      );
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.numberOfBookableEvents),
    rawValue: (data: EventsMetricsRow) => data.numberOfBookableEvents
  },
  {
    dataField: "featuredEvents",
    headerLabel: "Special Events",
    sort: true,
    valueInit: (events, dest) => {
      dest.numberOfFeaturedEvents = events.reduce(
        (acc: number, ev: EventMetricsSummary) => {
          return acc + (ev.featured ? 1 : 0);
        },
        0
      );
    },
    formatter: (data: EventsMetricsRow) =>
      formatThousands(data.numberOfFeaturedEvents),
    rawValue: (data: EventsMetricsRow) => data.numberOfFeaturedEvents
  }
];
